// src/utils/scale.ts

// 断点配置：根据你的设备调整这些数字
const BREAKPOINTS = [
    { max: 1366, scale: 0.8 },  // 小屏笔记本
    { max: 1512, scale: 0.9 },   // 14寸MacBook（你的设备）
    { max: 1680, scale: 0.95 },  // 主流笔记本
    { max: 1920, scale: 1.0 },   // 1080P显示器
    { max: 2560, scale: 1.1 },   // 2K显示器
    { max: 3840, scale: 1.2 },   // 4K显示器
    { max: 9999, scale: 1.3 }    // 超大屏
]

// 存储当前的缩放比例
// eslint-disable-next-line prefer-const
let currentScale = 1

// 主函数
export function initDisplayScale() {
    const updateScale = () => {
        const width = window.innerWidth

        // 找到匹配的缩放比例
        let scale = 1
        for (const bp of BREAKPOINTS) {
            if (width <= bp.max) {
                scale = bp.scale
                break
            }
        }

        // 应用缩放
        document.documentElement.style.setProperty('--display-scale', scale.toString())
        document.documentElement.style.fontSize = `${16 * scale}px`

        // 开发环境显示日志
        if (import.meta.env.DEV) {
            console.log(`[缩放] ${width}px → ${scale}x`)
        }

        return scale
    }

    // 检查是否已在 HTML 中初始化
    if (window.__INITIAL_SCALE__) {
        // 如果已初始化，只需要设置监听器
        window.addEventListener('resize', updateScale)
        return () => window.__INITIAL_SCALE__
    }

    // 初始化
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', updateScale)
    } else {
        updateScale()
    }

    // 监听窗口变化
    window.addEventListener('resize', updateScale)

    return updateScale
}

/**
 * 根据传入的值和当前屏幕大小，返回按缩放比例计算后的值
 * @param value - 原始值
 * @returns 按比例缩放后的值
 */
export function getScaledValue<T extends number | number[]>(value: T): T {
    if (Array.isArray(value)) {
        const scaledArray = value.map(v => v * currentScale)
        return scaledArray as T
    }
    
    // 确保是 number 类型
    if (typeof value === 'number') {
        return (value * currentScale) as T
    }
    
    return value
}

/**
 * 获取当前的缩放比例
 * @returns 当前缩放比例
 */
export function getCurrentScale(): number {
    return currentScale
}

/**
 * 监听缩放变化并执行回调
 * @param callback - 缩放变化时的回调函数，参数为新的缩放比例
 * @returns 取消监听的函数
 */
export function watchScaleChange(callback: (scale: number) => void): () => void {
    const handleResize = () => {
        const width = window.innerWidth
        let scale = 1
        for (const bp of BREAKPOINTS) {
            if (width <= bp.max) {
                scale = bp.scale
                break
            }
        }
        callback(scale)
    }

    window.addEventListener('resize', handleResize)
    
    // 立即执行一次
    handleResize()
    
    return () => window.removeEventListener('resize', handleResize)
}
